import React, { useEffect, useState } from "react";
import { encode } from "shopify-gid";
import { useMediaQuery } from "react-responsive";
import {
  Footer,
  Subeading,
  Heading,
  FooterRow,
  Container,
  CheckoutButton,
  EmptyContainer,
  EmptyHeading,
  EmptySubheading,
  StyledProductList,
  EmptyCheckoutButton,
  ProgressBar,
  ProgressContainer,
  ProgressBarSubheading,
  FreeShippingHeading,
  CaseContainer,
  Tag,
  TagIcon,
  DiscountCodeText,
  RemoveDiscountBtn,
  DiscountContainer,
  DiscountInput,
  AddDiscountButton,
  Loader,
  DiscountForm,
  DiscountError,
  TagContainer,
  TagWarning,
  Code,
  WarningText,
  CaseAccordionHeader, 
  CaseHeader, 
  CaseSubheader, 
  IsActive, 
  GridUpsells,
  CartIconsList,
  CartIconsListItem,
} from "./styles";
import {
  useReviewDrawer,
  useCart,
  useCheckout,
  useDiscount,
} from "src/context/siteContext";
import CartList from "src/components/CartList";
import ShippingIndicator from "src/components/ShippingIndicator";
import { Product } from "src/interfaces/sanity";
import { SmallDesktop } from "src/components/Responsive";
import MobileUpsells from "src/components/MobileUpsells";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import CaseCard from "src/components/CaseCard";
import { SanityImage } from "src/interfaces/sanity";
import Icon from "../Common/Icon";
import PortableText from '@sanity/block-content-to-react';
import chevronDown from 'src/images/chevron-down-2.svg';
import locale from 'src/locales';

export const size = {
  phone: 480,
};

async function shopifyFetch({ query, variables }: { query: string; variables?: any }) {
  const endpoint = `https://${process.env.GATSBY_SHOPIFY_STORE}/api/2023-10/graphql.json`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || "",
    },
    body: JSON.stringify({ query, variables }),
  });
  return response.json();
}

const PRODUCT_AVAILABILITY = `
  query getProductAvailability($id: ID!) {
    product(id: $id) {
      id
      variants(first: 100) {
        edges {
          node {
            id
            availableForSale
          }
        }
      }
    }
  }
`;

interface Props {
  info: {
    freeShippingMin: number;
    noFreeShipping?: boolean;
    upsellProducts?: Product[];
    textCopies?: {
      emptyCart: string;
      freeShipping: string;
      shippingCalculated: string;
      shippingHeading: string;
      subtotalHeading: string;
      freeShippingUnlocked: string;
    };
    emptyButtonLink?: string;
    emptyButtonText?: string;
    iconsList?: {
      _key?: string;
      _type?: string;
      icon?: {
        asset?: {
          url?: string;
          mimeType?: string;
        };
      };
      mobileVisible?: boolean;
      text?: string;
    }[];
  };
  productCases?: any;
  caseText?: any;
  caseSubheader?: any;
  caseImage?: SanityImage;
  caseTitle?: string;
  caseDescription?: string;
  caseSubtitle?: string;
}

interface DiscountInterface {
  input: string;
  loading: boolean;
  error: boolean;
}

const ga4Items = (lineItems: any) => {
  return lineItems?.map((item: any, idx: number) => {
    const { variant } = item;
    return {
      item_id: variant.sku,
      item_name: item.title,
      currency: process.env.GATSBY_CURRENCY_SYMBOL == "$" ? "USD" : "GBP",
      index: idx + 1,
      item_brand: "GroovePillows",
      item_variant: variant.title,
      price: variant.price?.amount || variant.priceV2?.amount,
      quantity: item.quantity,
    };
  });
};

const Cart = ({
  info,
  productCases,
  caseImage,
  caseTitle,
  caseDescription,
  caseSubtitle,
  caseSubheader,
  caseText
}: Props) => {
  const {
    checkout: { lineItems, discount, total, freeShipping, subtotalPriceV2 }
  } = useCart();
  const [discountState, setDiscountState] = useState<DiscountInterface>({
    input: "",
    loading: false,
    error: false,
  });
  const openCheckout = useCheckout();
  const { addDiscount, removeDiscount } = useDiscount();

  const beginCheckout = () => {
    dataLayer?.push({
      event: "begin_checkout",
      ecommerce: {
        items: ga4Items(lineItems),
      },
    });
    openCheckout();
  };

  const { closeReviews } = useReviewDrawer();
  const breakpoints = useBreakpoint();
  const [isActive, setIsActive] = useState(true);
  const isMobile = useMediaQuery({ maxWidth: size.phone });

  const {
    freeShippingMin,
    noFreeShipping,
    upsellProducts,
    textCopies,
    emptyButtonLink,
    emptyButtonText,
    iconsList,
  } = info;

  const pricedShip = parseFloat(total) < freeShippingMin;

  const onDiscountSubmit = async (e: any) => {
    e.preventDefault();
    setDiscountState((actualDiscount) => ({
      ...actualDiscount,
      error: false,
      loading: true,
    }));
    try {
      const updatedCart = await addDiscount(discountState.input);
      const discountCodes = updatedCart?.discountCodes || [];
      if (discountCodes.length !== 0) {
        const code = discountCodes[0]?.code;
        setDiscountState((actualDiscount) => ({
          ...actualDiscount,
          error: code?.toUpperCase() !== actualDiscount.input.toUpperCase(),
        }));
      } else {
        setDiscountState((actualDiscount) => ({
          ...actualDiscount,
          error: true,
        }));
      }
    } catch (err) {
      setDiscountState((actualDiscount) => ({
        ...actualDiscount,
        error: true,
      }));
    } finally {
      setDiscountState((actualDiscount) => ({
        ...actualDiscount,
        loading: false,
        input: "",
      }));
    }
  };

  const onDiscountChange = (e: any) => {
    setDiscountState((actualDiscount) => ({
      ...actualDiscount,
      input: e?.target?.value || "",
      error: actualDiscount.error ? false : actualDiscount.error,
    }));
  };

  useEffect(() => {
    if (lineItems?.length > 0) {
      dataLayer?.push({
        event: "view_cart",
        cart_total: total,
        currencyCode: process.env.GATSBY_CURRENCY_SYMBOL == "$" ? "USD" : "GBP",
        ecommerce: {
          items: ga4Items(lineItems),
        },
      });
    }
  }, []);

  const isUK = process.env.GATSBY_CURRENCY_SYMBOL === "£";

  const UpsellProducts = ({ id = "", heading = false, showBtn = false }) => {
    let cutProducts: Product[] = [];

    if (upsellProducts!.length > 0) {
      cutProducts = upsellProducts!.slice(0, 3);
    }


    return (
      <div id={id ?? "upsell-products"}>{
        breakpoints.smallDesktop ? (
          <>
            {heading && <EmptySubheading>Let’s add something.</EmptySubheading>}
            {cutProducts.length > 0 && (
              <StyledProductList
                products={cutProducts}
                addToCart={true}
                onClick={closeReviews}
              />
            )}
            {showBtn && emptyButtonLink && emptyButtonText && (
              <EmptyCheckoutButton to={emptyButtonLink} onClick={closeReviews}>
                {emptyButtonText}
              </EmptyCheckoutButton>
            )}
          </>
        ) : (
          <MobileUpsells
            products={upsellProducts}
            heading="Let's add something"
          />
        )
      }</div>)
  }

  const renderEmpty = () => {
    return (
      <EmptyContainer>
        <EmptyHeading>{textCopies.emptyCart}</EmptyHeading>
        <UpsellProducts heading={true} showButton={true} />
      </EmptyContainer>
    );
  };

  const productCasesIds: string[] = productCases?.map(
    (prod: any) => `${prod?.content?.shopify?.defaultVariant?.variantIdShopify}`
  );
  const newLineItems = lineItems?.filter(
    (item: any) =>
      !productCasesIds?.includes((item?.variant?.id))
  );

  const freeNeckPainId = "gid://shopify/ProductVariant/40074074456150";
  const neckPainProduct = lineItems?.find(item => item?.variant?.id === freeNeckPainId);
  const neckPainProductPrice = neckPainProduct?.variant?.price?.amount;
  const [canAddItems, setCanAddItems] = useState<{ [id: string]: boolean }>({});

  const getProductAvailability = async (prodId: string) => {
    const shopifyId = encode('Product', prodId, {
      accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
    });

    try {
      const { data } = await shopifyFetch({
        query: PRODUCT_AVAILABILITY,
        variables: { id: shopifyId }
      });

      const product = data?.product;
      const variants = product?.variants?.edges || [];
      const isAvailable = variants.some((edge: any) => edge.node.availableForSale);

      setCanAddItems(prevState => ({
        ...prevState,
        [prodId]: isAvailable, 
      }));
    } catch (e) {
      console.error("Error fetching availability", e);
    }
  };

  useEffect(() => {
    productCases?.length > 0 && productCases.map(async (item: any) => {
      const productId = item?._id?.replace("products-", "");
      getProductAvailability(productId);
    })
  }, [productCases]);

  const isAvailable = (productId: string) => canAddItems[productId] ?? false;


  return (
    <Container empty={!lineItems?.length > 0}>
      {!noFreeShipping && 
      <ShippingIndicator
        minPrice={freeShippingMin}
        total={total}
        items={lineItems?.length}
        freeShipping={freeShipping}
      />}
      {console.log("minnn",freeShippingMin)}
      {lineItems?.length > 0 ? (
        <>
          <CartList items={lineItems} />

          <GridUpsells id="grid-upsell">

          {productCases?.length > 0 && (
            <>
     
            <CaseAccordionHeader onClick={() => setIsActive(!isActive)}>
             
                 {caseText && (
                  <>
                    <CaseHeader>
                      <div className="header-subheader">
                        <span>{caseText}</span>
                        {caseSubheader && (
                          <CaseSubheader>
                            <PortableText blocks={caseSubheader} />
                          </CaseSubheader>
                        )}
                      </div>
                    </CaseHeader>
                  </>
                )} 
                <IsActive src={chevronDown} isActive={isActive} />
              </CaseAccordionHeader>
              {isActive && (
                <CaseContainer>
                  {productCases.map((pillow: any, idx: number) => {
                    const variantId =
                      pillow?.content?.shopify?.defaultVariant?.variantIdShopify;
                    let quantity = 0;
                    const itemInCart = lineItems?.find(
                      (item) =>
                        (item?.variant?.id) == variantId
                    );
                    if (itemInCart) {
                      quantity = itemInCart.quantity;
                    }
                    
                    const itemIsAvailable = isAvailable(pillow._id.replace("products-", ""));
                    
                    return (
                      <CaseCard
                        key={idx}
                        pillow={pillow}
                        canAdd={itemIsAvailable}
                        quantity={quantity}
                        lineItems={lineItems}
                        caseImage={caseImage}
                        caseDescription={caseDescription}
                        caseTitle={caseTitle}
                        caseSubtitle={caseSubtitle}
                      />
                    );
                  })}
                </CaseContainer>
              )}
            </>
          )}
          </GridUpsells>

          <UpsellProducts id="upsell-products-carousel" />

          <DiscountContainer
            id="discount-form"
            onSubmit={(e) => onDiscountSubmit(e)}
          >
            <DiscountForm>
              <DiscountInput
                value={discountState.input}
                error={discountState.error}
                onChange={(e) => onDiscountChange(e)}
                type="text"
                placeholder="Gift card or discount code"
              />
              <AddDiscountButton type="submit" disabled={!discountState.input}>
                {discountState.loading ? <Loader /> : "Apply"}
              </AddDiscountButton>
            </DiscountForm>
            {discountState.error && (
              <DiscountError>
                Enter a valid discount code or gift card
              </DiscountError>
            )}
          </DiscountContainer>
          {!isUK && discount?.has && (
            <TagContainer id="discount-tag">
              <Tag>
                <TagIcon>
                  <Icon name="tag" />
                </TagIcon>
                <DiscountCodeText>
                  {discount?.code}{" "}
                  {discount?.amount !== 0 && (
                    <span>
                      -{process.env.GATSBY_CURRENCY_SYMBOL}
                      {discount?.amount}
                    </span>
                  )}
                </DiscountCodeText>
                <RemoveDiscountBtn onClick={removeDiscount}>
                  <Icon name="close-thick" />
                </RemoveDiscountBtn>
              </Tag>
              {!discount?.applicable && !freeShipping && discount?.amount !== 0 && (
                <TagWarning>
                  <Icon name="warning" />
                  <WarningText>
                    <Code>{discount?.code}</Code> code isn't valid for the items
                    in your cart
                  </WarningText>
                </TagWarning>
              )}
            </TagContainer>
          )}
          

          <Footer>
            <>
              <SmallDesktop type="minWidth">
                <FooterRow>
                  <Subeading>{textCopies?.shippingHeading}</Subeading>
                  <Subeading>
                    {noFreeShipping ? textCopies?.shippingCalculated : <>
                    {pricedShip && !freeShipping
                      ? textCopies?.shippingCalculated
                      : textCopies?.freeShipping}</>}
                  </Subeading>
                </FooterRow>
              </SmallDesktop>
              <SmallDesktop type="maxWidth">
                {noFreeShipping ? 
                <Subeading>{textCopies?.shippingCalculated}</Subeading> : <>
                {parseFloat(total) < freeShippingMin && !freeShipping ? (
                  <ProgressContainer>
                    <ProgressBar
                      width={
                        (parseFloat(total).toFixed(2) / freeShippingMin) * 100
                      }
                    />
                    <ProgressBarSubheading>{`Spend ${process.env
                      .GATSBY_CURRENCY_SYMBOL +
                      (freeShippingMin - parseFloat(total)).toFixed(
                        2
                      )} more and receive free shipping!`}</ProgressBarSubheading>
                  </ProgressContainer>
                ) : (
                  <FooterRow>
                    <Subeading>{textCopies?.shippingHeading}</Subeading>
                    <FreeShippingHeading>
                      {textCopies?.freeShippingUnlocked}
                    </FreeShippingHeading>
                  </FooterRow>
                )}
                </>}
              </SmallDesktop>
              {isUK && discount?.has && (
                <FooterRow id="multibuyDiscount">
                  <Subeading>{locale.cart.multibuyDiscount}</Subeading>
                  <Heading>
                    {discount?.amount !== 0 && (
                      <span>
                        -{process.env.GATSBY_CURRENCY_SYMBOL}
                        {(discount?.amount - neckPainProductPrice).toFixed(2)}
                      </span>
                    )} 
                  </Heading>
                </FooterRow>
              )}
               {!isUK && discount?.amount > 0 && (
                <FooterRow>
                  <Subeading>{locale?.cart?.discount}</Subeading>
                  <Heading>
                    {discount?.amount !== 0 && (
                      <span>
                        -{process.env.GATSBY_CURRENCY_SYMBOL}
                        {(discount?.amount).toFixed(2)}
                      </span>
                    )} 
                  </Heading>
                </FooterRow>
              )}
              <FooterRow>
                <Subeading>{textCopies?.subtotalHeading}</Subeading>
                {isUK ? 
                <Heading>{process.env.GATSBY_CURRENCY_SYMBOL + Number(subtotalPriceV2.amount).toFixed(2)}</Heading> :
                <Heading>{process.env.GATSBY_CURRENCY_SYMBOL + Number(total).toFixed(2)}</Heading>
                }
              </FooterRow>
            </>
            <FooterRow id="checkoutButtonAndIcons">
              {iconsList && iconsList?.length > 0 && (
                <CartIconsList>
                  {iconsList?.map(iconsListItem => (
                    <CartIconsListItem key={iconsListItem?._key} mobileVisible={iconsListItem?.mobileVisible}>
                      <img src={iconsListItem?.icon?.asset?.url} alt={iconsListItem?.text + " icon"} height={40} />
                      <p>{iconsListItem?.text}</p>
                    </CartIconsListItem>
                  ))}
                </CartIconsList>
              )}
              <CheckoutButton onClick={beginCheckout} width={iconsList && iconsList?.length > 0 ? "50%" : "100%"}>Checkout{isMobile ? "🔒" : ""}</CheckoutButton>
            </FooterRow>
          </Footer>
        </>
      ) : (
        renderEmpty()
      )}
    </Container>
  );
};

export default Cart;
